// ONBOARD-EXAMS-v2-2026-10-16 §2: "Send exams" -- a second button beside the onboarding link, separate from the packet.
// Pick any of the exams (the PT one only for PT and PTA), an optional due date; it makes an exam-only link (the same
// hashed-token scheme as onboarding, its own table) and shows it once, to copy. Works with or without an onboarding
// session. Dark until the exams migration is applied (examsReady false): the button says so instead of failing.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EXAM_CATALOG, EXAM_BY_ID, examsForDiscipline, examLink, examRow, examGate, examTimeline, timelineText, reminderFor, PASS_PCT, retakeLock } from './lib/exams';
import { reminderMessage } from './lib/sendLink';
import { anotherAttemptMessage } from './lib/examReview';
import SendLink from './SendLink';
import ExamReview from './ExamReview';

const label = { fontSize: 11, color: '#475569', fontWeight: 600 };
const btn = (bg, fg) => ({ background: bg, color: fg, borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, minHeight: 44 });

export function SendExamsButton({ onOpen, ready = true, compact = false }) {
  return (
    <button className="nb" data-testid="send-exams-open" onClick={onOpen} disabled={!ready}
      title={ready ? 'Send competency exams (separate from the onboarding packet)' : 'Exams open once the database update is applied'}
      style={compact
        ? { border: 0, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#ecfeff', color: '#0e7490', opacity: ready ? 1 : 0.5 }
        : { background: '#0e7490', color: 'white', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700, opacity: ready ? 1 : 0.5 }}>
      Send exams
    </button>
  );
}

const today = (now) => { const d = new Date(now); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * candidate   the candidate (from their Onboarding tab), or null: then pick one from `candidates`
 * api         makeExamsApi(...);  config.baseUrl: the portal;  onSent(): reload the list
 */
export function SendExamsModal({ candidate = null, candidates = [], api, config = {}, onSent = () => {}, onClose, toast = () => {}, now = () => Date.now(),
  sendApi = null }) {
  const [pickedId, setPickedId] = useState(candidate ? candidate.id : '');
  const [query, setQuery] = useState('');
  const who = candidate || candidates.find((c) => String(c.id) === String(pickedId)) || null;
  const offered = useMemo(() => examsForDiscipline(who && who.specialty), [who]);
  const [chosen, setChosen] = useState(() => ['core-1', 'core-2', 'core-3']);
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const [made, setMade] = useState(null);
  const picks = chosen.filter((id) => offered.some((e) => e.id === id));
  const matches = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return candidates.filter((c) => words.every((w) => String(c.name || '').toLowerCase().includes(w)))
      .sort((a, b) => String(a.name).localeCompare(String(b.name))).slice(0, 50);
  }, [candidates, query]);

  const toggle = (id) => setChosen((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  const send = async () => {
    setFailure('');
    if (!who) { setFailure('Choose a candidate.'); return; }
    if (!picks.length) { setFailure('Choose at least one exam.'); return; }
    if (due && due < today(now())) { setFailure('The due date is in the past.'); return; }
    setBusy(true);
    try {
      const r = await api.send(who.id, picks, due || null);
      setMade({ link: examLink(r.token, config.baseUrl), expiresAt: r.expires_at, exams: picks, linkId: r.link_id });
      onSent();
    } catch (e) { setFailure(e.message || 'Could not send the exams'); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(made.link); toast('Exam link copied'); } catch { toast('Copy failed — select the link and copy it'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={onClose}>
      <div className="card" role="dialog" aria-label="Send exams" data-testid="send-exams-modal" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(520px, calc(100vw - 24px))', maxHeight: '85vh', overflow: 'auto', padding: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Send exams</h3>
        <p style={{ fontSize: 12, color: '#475569', marginBottom: 14 }}>
          A separate link for the competency exams. It never touches the onboarding packet, and the packet never needs it.
        </p>
        {!candidate && (
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="se-search">Candidate</label>
            <input id="se-search" data-testid="send-exams-search" className="input" placeholder="Search by name" value={query} disabled={!!made}
              onChange={(e) => setQuery(e.target.value)} style={{ marginTop: 4 }} />
            <select aria-label="Choose the candidate" data-testid="send-exams-candidate" value={pickedId} disabled={!!made}
              onChange={(e) => setPickedId(e.target.value)} style={{ marginTop: 6, width: '100%' }}>
              <option value="">Choose…</option>
              {matches.map((c) => <option key={c.id} value={c.id}>{c.name}{c.specialty ? ` (${c.specialty})` : ''}</option>)}
            </select>
          </div>
        )}
        {candidate && <p data-testid="send-exams-for" style={{ fontSize: 13, fontWeight: 600, margin: '0 0 10px' }}>For {candidate.name}{candidate.specialty ? ` (${candidate.specialty})` : ''}</p>}
        <fieldset style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', margin: '0 0 12px' }}>
          <legend style={label}>Exams</legend>
          {EXAM_CATALOG.map((e) => {
            // EXAMS-SEND-2026-10-16: off only for a known other discipline (OT, COTA, SLP, SLPA), with the reason on hover;
            // a blank or "Unknown" discipline can be sent it, with a one-line note
            const gate = examGate(e, who && who.specialty);
            return (
              <div key={e.id}>
                <label title={gate.why || undefined} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44, fontSize: 13, color: gate.allowed ? '#0f172a' : '#64748b' }}>
                  <input type="checkbox" data-testid={`send-exams-${e.id}`} checked={gate.allowed && chosen.includes(e.id)} disabled={!gate.allowed || !!made}
                    aria-describedby={gate.note ? `se-note-${e.id}` : undefined} onChange={() => toggle(e.id)} style={{ width: 18, height: 18 }} />
                  {e.title}{!gate.allowed ? ` (${e.disciplines.join(' and ')} only)` : ''}
                </label>
                {gate.note && <p id={`se-note-${e.id}`} data-testid={`send-exams-note-${e.id}`} style={{ fontSize: 12, color: '#92400e', margin: '0 0 6px 26px' }}>{gate.note}</p>}
              </div>
            );
          })}
        </fieldset>
        <label style={label} htmlFor="se-due">Due date (optional)</label>
        <input id="se-due" type="date" data-testid="send-exams-due" className="input" value={due} min={today(now())} disabled={!!made}
          onChange={(e) => setDue(e.target.value)} style={{ marginTop: 4, maxWidth: 220 }} />
        {failure && <p data-testid="send-exams-failure" style={{ fontSize: 12, color: '#b91c1c', marginTop: 10 }}>{failure}</p>}

        {/* EXAMS-SEND-2026-10-16: one Send dialog -- copy, text, email */}
        {made && sendApi && (
          <SendLink kind="exam" link={made.link} linkRef={made.linkId} candidate={who} api={sendApi} toast={toast} onSent={onSent} />
        )}
        {made && !sendApi && (
          <div data-testid="send-exams-made" style={{ marginTop: 16, background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, padding: 12 }}>
            <p style={{ fontSize: 12, color: '#0e7490', margin: '0 0 6px' }}>Shown once: copy it now and send it to the candidate.</p>
            <code data-testid="send-exams-url" style={{ wordBreak: 'break-all', fontSize: 13 }}>{made.link}</code>
            <div style={{ marginTop: 8 }}>
              <button className="nb" data-testid="send-exams-copy" onClick={copy} style={btn('#f1f5f9', '#334155')}>Copy</button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="nb" data-testid="send-exams-close" onClick={onClose} disabled={busy} style={btn('#f1f5f9', '#475569')}>{made ? 'Done' : 'Cancel'}</button>
          {!made && (
            <button className="nb" data-testid="send-exams-send" onClick={send} disabled={busy} style={btn('#0e7490', 'white')}>
              {busy ? 'Sending…' : 'Make the exam link'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const CHIP = {
  not_sent: ['#f8fafc', '#475569', '#e2e8f0'], sent: ['#ecfeff', '#0e7490', '#a5f3fc'], in_progress: ['#fffbeb', '#92400e', '#fde68a'],
  passed: ['#f0fdf4', '#15803d', '#bbf7d0'], failed: ['#fef2f2', '#b91c1c', '#fecaca'],
};
const chipStyle = (state) => { const [bg, fg, bd] = CHIP[state] || CHIP.not_sent; return { display: 'inline-block', fontSize: 11, fontWeight: 600,
  background: bg, color: fg, border: `1px solid ${bd}`, borderRadius: 6, padding: '1px 6px', marginRight: 4, marginBottom: 2 }; };

/** Open a passed exam's stored certificate (a short-lived signed URL). */
export async function openCertificate(api, path, toast = () => {}) {
  try { window.open(await api.certificateUrl(path), '_blank', 'noopener'); } catch (e) { toast(e.message || 'Could not open the certificate'); }
}

/** The Onboarding list's Exams cell: one chip per exam; a passed one downloads its certificate. */
export function ExamChips({ rows, api, toast, idPrefix }) {
  return rows.map((e) => (e.status.state === 'passed' && e.status.certPath ? (
    <button key={e.id} className="nb" data-testid={`${idPrefix}-${e.id}`} data-state="passed"
      title={`${e.title}: download the certificate`} aria-label={`${e.title}: ${e.status.text}. Download the certificate`}
      onClick={(ev) => { ev.stopPropagation(); openCertificate(api, e.status.certPath, toast); }}
      style={{ ...chipStyle('passed'), cursor: 'pointer' }}>{e.short}: {e.status.text} ⤓</button>
  ) : (
    <span key={e.id} data-testid={`${idPrefix}-${e.id}`} data-state={e.status.state} title={e.title} style={chipStyle(e.status.state)}>{e.short}: {e.status.text}</span>
  )));
}

/**
 * The candidate's Onboarding tab: their exams, separate from the packet. open/setOpen: the Send exams modal (the Link
 * box's button opens it too). Hidden entirely when the CRM has no exams api; "not ready" until the migration is applied.
 */
export function ExamsSection({ candidate, api, config = {}, toast = () => {}, open = false, setOpen = () => {}, onReady = () => {}, sendApi = null,
  now = () => Date.now() }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // §3 (NIGHT-DIRECTSTAFF-2026-10-17): the manual reminder -- 'ask' explains it, 'made' is the new link in the Send box
  const [remind, setRemind] = useState(null);
  // NIGHT-DIRECTSTAFF-2026-10-19 §3: the review of a finished attempt { attemptId, examId }
  const [reviewing, setReviewing] = useState(null);
  // §3 (10-21): "Allow another attempt" -- { examId, step: 'ask' | 'made', link, linkId }
  const [another, setAnother] = useState(null);
  const lockOf = (examId) => (data && data.ready ? retakeLock(examId, data.links, data.attempts) : { locked: false });
  const allowAnother = async () => {
    setBusy(true);
    try {
      const r = await api.allowAnother(candidate.id, another.examId);
      setAnother({ ...another, step: 'made', link: examLink(r.token, config.baseUrl), linkId: r.link_id });
      if (!r.logged) toast('Link made, but it was not logged on the timeline');
      await reload();
    } catch (e) { toast(e.message || 'Could not allow another attempt'); } finally { setBusy(false); }
  };
  const lastFinished = (examId) => { if (!data || !data.attempts) return null; const done = data.attempts.filter((a) => a.exam_id === examId && a.submitted_at);
    return done.find((a) => a.passed) || done.sort((a, b) => b.attempt_no - a.attempt_no)[0] || null; };
  const reload = useCallback(async () => {
    try { setData(await api.load(candidate.id)); } catch (e) { setData({ error: e.message }); }
  }, [api, candidate.id]);
  useEffect(() => { setData(null); reload(); }, [reload]);
  const ready = !!(data && data.ready);
  useEffect(() => { onReady(ready); }, [ready]);   // onReady is the parent's setter: stable
  const rows = ready ? examRow(candidate, data.links, data.attempts, now()) : [];
  const openLinks = ready ? data.links.filter((l) => l.status === 'open' && new Date(l.expires_at).getTime() > now()) : [];
  const due = ready ? reminderFor(candidate, data.links, data.attempts, now()) : null;
  const names = (ids) => ids.map((id) => (EXAM_BY_ID[id] ? EXAM_BY_ID[id].short : id));
  const makeReminder = async () => {
    setBusy(true);
    try {
      const r = await api.send(candidate.id, remind.exams, remind.dueOn);
      const link = examLink(r.token, config.baseUrl);
      // §5 (10-21): Text first when they consented; Email first (Text off) when they didn't
      let consent = null;
      try { const lead = sendApi && sendApi.findLead ? await sendApi.findLead(candidate) : null; consent = lead ? lead.sms_consent === true && lead.sms_opt_out !== true : null; } catch { consent = null; }
      setRemind({ ...remind, step: 'made', link, linkId: r.link_id, consent });
      await reload();
    } catch (e) { toast(e.message || 'Could not make the reminder link'); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    setBusy(true);
    try { await api.revoke(id); toast('Exam link turned off'); await reload(); } catch (e) { toast(e.message); } finally { setBusy(false); }
  };
  return (
    <section data-testid="onb-exams" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>Exams <span data-testid="onb-exams-pass-line" style={{ fontWeight: 400, color: '#475569' }}>· pass line {PASS_PCT}%</span></h3>
        <SendExamsButton compact ready={ready} onOpen={() => setOpen(true)} />
      </div>
      {!data && <p style={{ fontSize: 12, color: '#475569' }}>Loading…</p>}
      {data && data.error && <p data-testid="onb-exams-error" style={{ fontSize: 12, color: '#b91c1c' }}>{data.error}</p>}
      {data && data.ready === false && <p data-testid="onb-exams-not-ready" style={{ fontSize: 12, color: '#92400e' }}>Exams open once the database update is applied.</p>}
      {ready && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', fontSize: 13 }}>
          {rows.map((e) => (
            <li key={e.id} data-testid={`onb-exam-${e.id}`} data-state={e.status.state}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span>{e.title}
                {(() => { const w = timelineText(examTimeline(e.id, data.links, data.attempts)); return w
                  ? <span data-testid={`onb-exam-when-${e.id}`} style={{ display: 'block', fontSize: 12, color: '#475569' }}>{w}</span> : null; })()}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={chipStyle(e.status.state)}>{e.status.text}</span>
                {e.status.state === 'failed' && lockOf(e.id).locked && api.allowAnother && (
                  <button className="nb" data-testid={`onb-exam-allow-${e.id}`} onClick={() => setAnother({ examId: e.id, step: 'ask' })}
                    style={{ fontSize: 12, color: '#92400e', padding: '0 6px', minHeight: 44, background: 'transparent', fontWeight: 700 }}>Allow another attempt</button>
                )}
                {(e.status.state === 'passed' || e.status.state === 'failed') && api.review && lastFinished(e.id) && (
                  <button className="nb" data-testid={`onb-exam-review-${e.id}`} onClick={() => setReviewing({ attemptId: lastFinished(e.id).id, examId: e.id })}
                    style={{ fontSize: 12, color: '#1d4ed8', padding: '0 6px', minHeight: 44, background: 'transparent' }}>Review</button>
                )}
                {e.status.state === 'passed' && e.status.certPath && (
                  <button className="nb" data-testid={`onb-exam-cert-${e.id}`} onClick={() => openCertificate(api, e.status.certPath, toast)}
                    style={{ fontSize: 12, color: '#1d4ed8', padding: 0 }}>Download certificate</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {openLinks.map((l) => (
        <p key={l.id} data-testid={`onb-exam-link-${l.id}`} style={{ fontSize: 12, color: '#475569', margin: '8px 0 0' }}>
          Exam link sent {new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {l.due_on ? `, due ${new Date(`${l.due_on}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
          {' · '}<button className="nb" data-testid={`onb-exam-revoke-${l.id}`} disabled={busy} onClick={() => revoke(l.id)}
            style={{ fontSize: 12, color: '#b91c1c', padding: 0 }}>Turn off</button>
        </p>
      ))}
      {/* §3: sent 3+ days ago and not finished -> Remind. Manual only: nothing is sent until a button in the Send box is pressed. */}
      {ready && due && !remind && (
        <div data-testid="onb-exam-remind-due" style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10,
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, color: '#78350f' }}>{names(due.exams).join(', ')}: sent {new Date(due.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, not finished.</span>
          <button className="nb" data-testid="onb-exam-remind" onClick={() => setRemind({ ...due, step: 'ask' })} style={btn('#b45309', 'white')}>Remind</button>
        </div>
      )}
      {remind && remind.step === 'ask' && (
        <div data-testid="onb-exam-remind-ask" style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12 }}>
          <p style={{ fontSize: 13, color: '#78350f', margin: '0 0 8px' }}>
            A reminder for {names(remind.exams).join(', ')}. Exam links are stored scrambled, so the first link can't be shown again: this makes a
            new link to the same exams{remind.dueOn ? `, due ${new Date(`${remind.dueOn}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}.
            They pick up where they left off, and the first link keeps working. Nothing is sent until you press Text or Email.</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="nb" data-testid="onb-exam-remind-make" disabled={busy} onClick={makeReminder} style={btn('#0e7490', 'white')}>{busy ? 'Making…' : 'Make the reminder'}</button>
            <button className="nb" data-testid="onb-exam-remind-cancel" disabled={busy} onClick={() => setRemind(null)} style={btn('#f1f5f9', '#475569')}>Cancel</button>
          </div>
        </div>
      )}
      {remind && remind.step === 'made' && (
        <div data-testid="onb-exam-remind-made">
          {sendApi ? (
            <SendLink kind="exam" link={remind.link} linkRef={remind.linkId} candidate={candidate} api={sendApi} toast={toast} onSent={reload}
              primary={remind.consent === true ? 'text' : 'email'} textConsent={remind.consent === false ? false : null}
              initialBody={reminderMessage(candidate.name, remind.link, names(remind.exams))} />
          ) : (
            <p style={{ fontSize: 13, marginTop: 10 }}><code data-testid="send-exams-url" style={{ wordBreak: 'break-all' }}>{remind.link}</code></p>
          )}
          <button className="nb" data-testid="onb-exam-remind-done" onClick={() => setRemind(null)} style={{ ...btn('#f1f5f9', '#475569'), marginTop: 8 }}>Done</button>
        </div>
      )}
      {reviewing && (
        <ExamReview candidate={candidate} attemptId={reviewing.attemptId} examId={reviewing.examId} load={(id) => api.review(id, config.baseUrl)} locked={lockOf(reviewing.examId).locked}
          api={api} config={config} sendApi={sendApi} toast={toast} onSent={reload} onClose={() => setReviewing(null)} />
      )}
      {/* §3 (10-21): two attempts used -> staff may allow one more: a new link to that exam, logged on the timeline */}
      {another && another.step === 'ask' && (
        <div data-testid="onb-exam-allow-ask" role="alertdialog" aria-label="Allow another attempt" style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12 }}>
          <p style={{ fontSize: 13, color: '#78350f', margin: '0 0 8px' }}>{EXAM_BY_ID[another.examId] ? EXAM_BY_ID[another.examId].short : another.examId}: both attempts are used.
            This makes a new link good for <strong>one</strong> more attempt (the 24-hour wait still applies) and notes it on their timeline. Nothing is sent until you press Text or Email.</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="nb" data-testid="onb-exam-allow-yes" disabled={busy} onClick={allowAnother} style={btn('#92400e', 'white')}>{busy ? 'Making…' : 'Allow another attempt'}</button>
            <button className="nb" data-testid="onb-exam-allow-no" disabled={busy} onClick={() => setAnother(null)} style={btn('#f1f5f9', '#475569')}>Cancel</button>
          </div>
        </div>
      )}
      {another && another.step === 'made' && (
        <div data-testid="onb-exam-allow-made">
          {sendApi ? (
            <SendLink kind="exam" link={another.link} linkRef={another.linkId} candidate={candidate} api={sendApi} toast={toast} onSent={reload}
              initialBody={anotherAttemptMessage({ name: candidate.name, examId: another.examId, link: another.link })} />
          ) : <p style={{ fontSize: 13, marginTop: 10 }}><code style={{ wordBreak: 'break-all' }}>{another.link}</code></p>}
          <button className="nb" onClick={() => setAnother(null)} style={{ ...btn('#f1f5f9', '#475569'), marginTop: 8 }}>Done</button>
        </div>
      )}
      {open && <SendExamsModal candidate={candidate} api={api} config={config} toast={toast} onSent={reload} onClose={() => setOpen(false)} sendApi={sendApi} />}
    </section>
  );
}
